import { DownloadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Button, Checkbox, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import {
  usePlatformSelect,
  useStoreInfoSelect,
  useStoreTypeSelect,
} from 'views/Dashboard/ImplementData/components/Filter/Provider';
import { useCustomSelect } from '../components/AssistantPerf/Content';
import { FilterValue } from './Filter';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import FlexStyled from 'components/styled/flex';
import { DatePicker } from 'components/DatePicker';
import dayjs from 'lib/dayjs';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { exportStats } from './service';
import { cloneDeep } from 'lodash-es';

interface Props {
  initialValue: FilterValue;
  defaultChecked: string;
  value2Body: any;
}

export const ExportData = (props: Props) => {
  const { initialValue, value2Body, defaultChecked } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const [checkedList, setCheckedList] = useState<string[]>([defaultChecked]);
  const systemLanguage = useSystemLanguageValue();
  const [loading, setLoading] = useState(false);

  const [filterValue, setFilterValue] = useState<FilterValue>(cloneDeep(initialValue));

  const { runAsync: download } = useExportFile();

  const options = [
    {
      label: t('aide.人工接待评分统计'),
      value: 'ASSISTANT',
    },
    {
      label: t('aide.人机协同接待评分统计'),
      value: 'BOT',
    },
  ];

  const { departments, platform, storeType, storeInfo } = filterValue;

  const departmentSelect = (
    <DepartmentsSelect
      dashboardType="performance"
      value={departments}
      onChange={(departments) => {
        setFilterValue((pre) => ({
          ...pre,
          departments,
          platform: undefined,
          storeType: undefined,
          storeInfo: {},
          assistantIds: [],
          assistantId: undefined,
        }));
      }}
    />
  );

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments,
    scene: 'ASSISTANT_PERFORMANCE',
    showPlatformAll: true,
    value: platform || 'ALL',
    onChange: (platform) => {
      setFilterValue((pre) => ({
        ...pre,
        platform: platform === 'ALL' ? undefined : platform,
        storeInfo: {},
      }));
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: storeType,
    onChange: (storeType) => {
      setFilterValue((pre) => ({
        ...pre,
        storeType,
        storeInfo: {},
      }));
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform: platform || 'ALL',
    departments,
    platformOptions,
    storeType,
    storeInfo,
    onChange: (storeInfo) => {
      setFilterValue((pre) => ({
        ...pre,
        storeInfo,
      }));
    },
    scene: 'ASSISTANT_PERFORMANCE',
  });

  const { customSelect } = useCustomSelect({
    departments: filterValue.departments,
    mode: 'multiple',
    value: filterValue.assistantIds,
    onChange: (assistantIds) => {
      setFilterValue((pre) => ({
        ...pre,
        assistantIds,
      }));
    },
  });

  const handleExport = () => {
    const exportData = (receptionType: string) => {
      value2Body({
        ...filterValue,
        receptionType,
        language: systemLanguage,
      })
        .then((body: any) => {
          if (!body) {
            return Promise.resolve();
          }
          return download(() => exportStats(body));
        })
        .finally(() => {
          setLoading(false);
        });
    };
    setLoading(true);

    Promise.all(checkedList.map((value) => exportData(value))).finally(() => {
      setLoading(false);
    });
  };

  const modal = (
    <Modal
      title={t('aide.导出接待评分')}
      width={720}
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={
        <FlexStyled.Flex className="justify-end">
          <span className="mr-4">
            {t('export_pref_modal_selected_x', { x: checkedList?.length ?? 0 })}
          </span>
          <Button type="default" disabled={loading} onClick={() => setVisible(false)}>
            {t('translation.取消')}
          </Button>
          <Button
            type="primary"
            onClick={handleExport}
            loading={loading}
            disabled={!checkedList?.length}
          >
            {t('export_pref_modal_button_export')}
          </Button>
        </FlexStyled.Flex>
      }
    >
      <div className="flex flex-col gap-2 border border-[#f0f0f0] border-solid rounded-md mt-4">
        <div className="flex items-center border-t-0 border-l-0 border-r-0 border-b border-[#f0f0f0] border-solid px-2 py-3 flex-wrap gap-4">
          {departmentSelect}
          {platformSelect}
          {storeTypeSelect}
          {storeInfoSelect}
          {customSelect}
        </div>
        <div className="flex flex-row gap-2 px-4 py-3 items-center">
          <span>{t('ViewDashboard.日期')}</span>
          <DatePicker.RangePicker
            allowClear={false}
            className="w-[380px]"
            disabledDate={(current) => {
              return current && (current < dayjs().subtract(1, 'year') || current > dayjs());
            }}
            value={[dayjs(filterValue.startTime), dayjs(filterValue.endTime)]}
            onChange={(dateRange) => {
              const [start, end] = dateRange || [];

              if (!start || !end) {
                return;
              }

              setFilterValue((pre) => ({
                ...pre,
                startTime: start.startOf('days').valueOf(),
                endTime: end.endOf('days').valueOf(),
              }));
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 border border-[#f0f0f0] border-solid rounded-md mt-4">
        <div className="flex items-center border-t-0 border-l-0 border-r-0 border-b border-[#f0f0f0] border-solid px-2 py-3">
          <Checkbox
            indeterminate={checkedList.length > 0 && checkedList.length < options.length}
            onChange={(e) => {
              setCheckedList(e.target.checked ? options.map((opt) => opt.value) : []);
            }}
            checked={checkedList.length === options.length}
          >
            {t('全部数据')}
          </Checkbox>
        </div>
        <div className="flex flex-row gap-2 px-4 py-3">
          <Checkbox.Group
            value={checkedList}
            onChange={(value) => setCheckedList(value as string[])}
          >
            {options.map((opt) => (
              <Checkbox key={opt.value} value={opt.value}>
                <span>{opt.label}</span>
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      </div>
    </Modal>
  );

  useEffect(() => {
    if (visible) {
      setFilterValue(cloneDeep(initialValue));
      setCheckedList([defaultChecked]);
    }
  }, [initialValue, visible, defaultChecked]);

  return (
    <>
      <Button type="ghost" icon={<DownloadOutlined />} onClick={() => setVisible(true)}>
        {t('aide.导出接待评分')}
      </Button>
      {modal}
    </>
  );
};
